import type { ReactNode } from 'react';
import { useReducedMotion } from 'framer-motion';

export type MarqueeProps = {
  children: ReactNode;
  durationSec?: number;
  className?: string;
  pauseOnHover?: boolean;
};

/**
 * Thin wrapper over the EXISTING ticker keyframe (sets --ticker-duration)
 * for the masthead crawl and logo strip — the single permitted idle loop.
 * Children are duplicated for a seamless -50% translate.
 *
 * Adds no new keyframe. Static under prefers-reduced-motion.
 */
export function Marquee({
  children,
  durationSec = 90,
  className,
  pauseOnHover = false,
}: MarqueeProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return (
      <div className={`overflow-hidden ${className ?? ''}`.trim()}>
        <div className="flex w-max">{children}</div>
      </div>
    );
  }

  return (
    <div
      className={`group overflow-hidden ${className ?? ''}`.trim()}
      style={{ '--ticker-duration': `${durationSec}s` } as React.CSSProperties}
    >
      <div
        className={[
          'flex w-max animate-ticker',
          pauseOnHover ? 'group-hover:[animation-play-state:paused]' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="flex shrink-0">{children}</div>
        <div className="flex shrink-0" aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
